import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  ArrowLeftIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  EyeIcon,
  MapPinIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import CreateTopicForm from './CreateTopicForm';

interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

interface Topic {
  id: string;
  title: string;
  isPinned: boolean;
  isLocked: boolean;
  views: number;
  createdAt: string;
  author: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  _count: {
    posts: number;
  };
}

const CategoryTopicsPage: React.FC = () => {
  const { forumSlug, categoryId } = useParams<{
    forumSlug: string;
    categoryId: string;
  }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCategoryAndTopics();
  }, [categoryId, page]);

  const fetchCategoryAndTopics = async () => {
    try {
      setLoading(true);
      // Fetch topics
      const topicsResponse = await axios.get(
        `/api/forums/categories/${categoryId}/topics`,
        {
          params: { page, limit: 20 },
        }
      );
      setTopics(topicsResponse.data.topics);
      setTotalPages(topicsResponse.data.pagination.totalPages);

      // Fetch category info (we can get it from the first topic's category)
      if (topicsResponse.data.topics.length > 0) {
        // For now, we'll fetch the forum to get category details
        const forumResponse = await axios.get(`/api/forums/slug/${forumSlug}`);
        const cat = forumResponse.data.categories.find(
          (c: Category) => c.id === categoryId
        );
        setCategory(cat || null);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/forums/${forumSlug}`)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Forum
        </button>
      </div>

      {/* Category Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            {category?.icon && (
              <span className="text-4xl">{category.icon}</span>
            )}
            {category?.name || 'Topics'}
          </h1>
          {category?.description && (
            <p className="mt-2 text-gray-600">{category.description}</p>
          )}
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          New Topic
        </button>
      </div>

      {/* Create Topic Form */}
      {showCreateForm && (
        <div className="mb-8">
          <CreateTopicForm
            categoryId={categoryId!}
            forumSlug={forumSlug!}
            onSuccess={() => {
              setShowCreateForm(false);
              fetchCategoryAndTopics();
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Topics List */}
      <div className="bg-white rounded-lg shadow">
        {topics.length === 0 ? (
          <div className="p-12 text-center">
            <ChatBubbleLeftIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No topics yet
            </h3>
            <p className="text-gray-600 mb-6">
              Be the first to start a conversation!
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Topic
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() =>
                  navigate(`/forums/${forumSlug}/topics/${topic.id}`)
                }
                className="w-full p-6 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-start gap-4">
                  {/* Author Avatar */}
                  <img
                    src={
                      topic.author.avatar ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        topic.author.firstName + ' ' + topic.author.lastName
                      )}`
                    }
                    alt={topic.author.username}
                    className="h-10 w-10 rounded-full flex-shrink-0"
                  />

                  {/* Topic Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {topic.isPinned && (
                        <MapPinIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      )}
                      {topic.isLocked && (
                        <LockClosedIcon className="h-4 w-4 text-red-600 flex-shrink-0" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {topic.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>
                        by {topic.author.firstName} {topic.author.lastName}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {new Date(topic.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Topic Stats */}
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <ChatBubbleLeftIcon className="h-4 w-4" />
                      <span>{topic._count.posts}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <EyeIcon className="h-4 w-4" />
                      <span>{topic.views}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CategoryTopicsPage;
